import moment from "moment";
import * as Yup from "yup";

import {
  applicationTypes,
  maxLtContractDemandLoad,
  maxWaterDemand,
  minHtContractDemandLoad,
  regexForName,
} from "common/constant";
import { getOptionValue } from "common/enums";
import { getConsumerNumberValidation } from "./getConsumerNumberValidation";

const consumerDetailsSchema = Yup.object({
  consumerNumber: getConsumerNumberValidation(Yup.string()),
  consumerName: Yup.string().required("Consumer name is required"),

  electricityDuty: Yup.string().when("utilityId", {
    is: (utilityId) => getOptionValue("utilityOptions", "Power") === utilityId,
    then: (schema) => schema.required("Electricity duty is required"),
  }),

  serviceConnectionCharges: Yup.string().when("utilityId", {
    is: (utilityId) => getOptionValue("utilityOptions", "Power") === utilityId,
    then: (schema) => schema.required("Service connection charges is required"),
  }),

  // securityDeposit: Yup.string().when("utilityId", {
  //   is: (utilityId) => getOptionValue("utilityOptions", "Power") === utilityId,
  //   then: (schema) => schema.required("Security deposit is required"),
  // }),
  securityDeposit: Yup.string().test(
  "security-deposit-check",
  function (value) {
    const { utilityId, applicationType } = this.parent;
    const powerOption = getOptionValue("utilityOptions", "Power");

    if (powerOption === utilityId) {
      if (!value) {
        if (applicationType === 1) {
          return this.createError({ message: "Security deposit is required" });
        } else if (applicationType === 2) {
          return this.createError({ message: "Temporary connection deposit required" });
        }
      }
    }
    return true;
  }
),

  sourceOfPowerId: Yup.string().when("utilityId", {
    is: (utilityId) => getOptionValue("utilityOptions", "Power") === utilityId,
    then: (schema) => schema.required("Source of power is required"),
  }),
  salesOrder: Yup.string().when("utilityId", {
    is: (utilityId) => getOptionValue("utilityOptions", "Power") !== utilityId,
    then: (schema) =>
      schema.required("Sales order is required").test({
        name: "isValidSalesOrder",
        test: (value) => /^[a-zA-Z0-9]{10}$/.test(value),
        message: "Sales order length must be 10 digits",
      }),
  }),
  serialNumber: Yup.string().when("utilities", {
    is: (utilities) =>
      utilities.some(
        ({ value }) => value === getOptionValue("utilityOptions", "Solid Waste")
      ),
    then: (schema) =>
      schema.required("Serial number is required").test({
        name: "isValidSerialNumber",
        test: (value) => /^[a-zA-Z0-9]*$/.test(value),
        message: "Please enter valid serial number",
      }),
  }),

  disconnectionDate: Yup.date().test("minEndDate", "", (_, context) => {
    const [{ value: { disconnectionDate, dateOfConnection } = {} } = {}] =
      context.from || [];

    if (
      dateOfConnection &&
      disconnectionDate?.isSameOrBefore(dateOfConnection)
    ) {
      return context.createError({
        message: "Disconnection date should be after connection start date",
      });
    }
    return true;
  }),
});

const applicantDetailsSchema = Yup.object({
  applicant: Yup.object({
    applicantTypeId: Yup.string().required("Applicant Type is required"),
    applicantName: Yup.string()
      .matches(regexForName, "Please enter a valid name")
      .required("Applicant Name is required"),
    panNumber: Yup.string()
      .matches(
        /^[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}$/,
        "Please enter a valid Pan number"
      )
      .test("isPanNumberRequired", "", function () {
        const [
          { value: current = null } = {},
          { value: { extraValues: { applicantType } } = {} } = {},
        ] = this.from || [];

        const isIndividualApplicantType =
          applicantType.find(
            (applicant) => applicant.name.toLowerCase() === "individual"
          )?.id === current.applicantTypeId;
        if (!isIndividualApplicantType && !current.panNumber) {
          return this.createError({
            message: "Pan number is required",
          });
        }
        return true;
      }),
    isGSTRegistered: Yup.string().required("GST is required"),
    gstNumber: Yup.string()
      .test("isGstRequired", "", function (_, context) {
        const [{ value: { isGSTRegistered, gstNumber } } = {}] =
          context.from || [];
        if (isGSTRegistered && !gstNumber) {
          return context.createError({
            message: "GST number is required",
          });
        }
        return true;
      })
      .matches(
        /^[0-9]{2}[A-Za-z]{5}[0-9]{4}[A-Za-z]{1}[1-9A-Za-z]{1}[Zz][0-9A-Za-z]{1}$/,
        "Please enter a valid GST number"
      ),
    occupancyTypeId: Yup.string().required("Occupancy type is required"),
    addressType: Yup.string().required("Address type is required"),
    city: Yup.object({
      id: Yup.number().required("City is required"),
    }),
    correspondenceAddress: Yup.string()
      .matches(/^[\w]+[\w\s.,-]*[\w\s.,-]+/, "Please enter valid address")
      .required("Correspondence Address is required"),
    pinCode: Yup.string()
      .matches(/^\d{6}$/, "Please enter valid pincode")
      .required("Pincode is required"),
    email: Yup.string()
      .email("Please enter valid email")
      .required("Email is required"),
    mobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid mobile number")
      .required("Mobile number is required"),
    alternateMobileNumber: Yup.string()
      .matches(/^\d{10}$/, "Please enter valid mobile number")
      .test("isValidAlternativeNumber", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        const isSameNumber =
          +current.mobileNumber === +current.alternateMobileNumber;
        if (isSameNumber) {
          return this.createError({
            message: "Alternate mobile number can't be same as mobile number.",
          });
        }
        return true;
      }),
    alternateEmail: Yup.array().test("isValidAlternateEmail", "", function () {
      const [{ value: current = null } = {}] = this.from || [];

      if (
        !!current.alternateEmail.length &&
        !current.alternateEmail.every((email) =>
          email?.match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          )
        )
      ) {
        return this.createError({
          message: "Enter valid emails",
        });
      }
      if (current.alternateEmail.some((email) => email === current.email)) {
        return this.createError({
          message: "Alternate email can't be same as email.",
        });
      }
      return true;
    }),

    landLine: Yup.string().matches(
      /^\d{8,11}$/,
      "Please enter a valid landline number"
    ),
  }),
});
const createUtilityAddressSchema = ({
  applicationTypeId,
} = {}) =>
  Yup.object({
    utilityAddress: Yup.object({
      ...(applicationTypeId === 1 && {
        buildingIdentificationName: Yup.string().required(
          "Building identification name is required"
        ),
      }),
      premiseTypeId: Yup.string().required("Premise type is required"),
      premiseUsagePurpose: Yup.string().required(
        "Premise usage purpose is required"
      ),
      premisesAreaId: Yup.string().required("Premise Area is required"),
      zoneName: Yup.string().required("Zone is required"),
      blockName: Yup.string().required("Block is required"),
      buildingName: Yup.string().required("Building is required"),
      roadName: Yup.string().required("Road name is required"),
      // floors: Yup.array().min(1, "Floor is required"),
      otherZone: Yup.string().test("isOtherZone", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.zoneName === "other" && !current.otherZone) {
          return this.createError({
            message: "Zone is required",
          });
        }
        return true;
      }),
      otherBlock: Yup.string().test("isOtherBlock", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.blockName === "other" && !current.otherBlock) {
          return this.createError({
            message: "Block is required",
          });
        }
        return true;
      }),
      otherBuilding: Yup.string().test("isOtherBuilding", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.buildingName === "other" && !current.otherBuilding) {
          return this.createError({
            message: "Building is required",
          });
        }
        return true;
      }),

      otherRoad: Yup.string().test("isOtherRoad", "", function () {
        const [{ value: current = null } = {}] = this.from || [];

        if (current.roadName === "other" && !current.otherRoad) {
          return this.createError({
            message: "Road is required",
          });
        }
        return true;
      }),

      unitName: Yup.string().required("Unit ∕ Flat No is required"),
      dcsDomesticPremiseType: Yup.string().test(
        "isDcsDomesticPremiseTypeValid",
        "",
        function () {
          const [
            { value: current = null } = {},
            { value: { utilities, extraValues: { premiseTypes } } = {} } = {},
          ] = this.from || [];

          if (
            utilities.some(
              ({ value }) => value === getOptionValue("utilityOptions", "DCS")
            )
          ) {
            if (
              premiseTypes
                ?.find(({ id }) => id === current.premiseTypeId)
                ?.name.toLowerCase() === "domestic" &&
              !current.dcsDomesticPremiseType
            ) {
              return this.createError({
                message: "Dcs Connection is required",
              });
            }
            return true;
          }
          return true;
        }
      ),
      dcsIndividualPremiseType: Yup.string().test(
        "isDcsIndividualPremiseTypeValid",
        "",
        function () {
          const [{ value: current = null } = {}] = this.from || [];
          if (
            current.dcsDomesticPremiseType ===
              getOptionValue("dcsConnectionTypeEnum", "Individual Units") &&
            !current.dcsIndividualPremiseType
          ) {
            return this.createError({
              message: "Individual Units Type is required",
            });
          }
          return true;
        }
      ),
    }),
  });

const utilityAddressSchema = createUtilityAddressSchema();
const otherUtilityAddressSchema = (applicationTypeId) =>
  createUtilityAddressSchema({ applicationTypeId });

const connectionLoadSchema = Yup.object({
  salesOrder: Yup.string().when("connectionLoad.backupRequired", {
    is: true,
    then: (schema) =>
      schema
        .required("Sales order is required")
        .test(
          "isValidSalesOrder",
          "Sales order length must be 10 alphanumeric characters",
          (value) => /^[a-zA-Z0-9]{10}$/.test(value || "")
        ),
    otherwise: (schema) => schema,
  }),
  connectionLoad: Yup.object({
    backupkVA: Yup.number().when("backupRequired", {
      is: true,
      then: (schema) =>
        schema
          .required("Backup voltage is required")
          .test("isValidBackupVoltage", "", function () {
            const [{ value: current = null } = {}] = this.from || [];
            if (current.backupkVA <= 0) {
              return this.createError({
                message: "Backup voltage must be positive",
              });
            }
            if (current.backupkVA >= 100000) {
              return this.createError({
                message: "Maximum value allowed for backup voltage is 99999",
              });
            }
            return true;
          }),
      otherwise: (schema) => schema,
    }),
    loadDetails: Yup.string().required("Load details is required"),
    supplyVoltage: Yup.string().required("Supply voltage is required"),
    tention: Yup.string().required("Please select power requirement"),
    timeShift: Yup.string().test("isTimeShiftRequired", "", function () {
      const [
        { value: current = null } = {},
        {
          value: {
            extraValues: { premiseIdsForTimeShift },
            utilityAddress: { premiseTypeId },
          } = null,
        } = {},
      ] = this.from || [];

      if (
        premiseIdsForTimeShift.includes(premiseTypeId) &&
        !current.timeShift
      ) {
        return this.createError({
          message: "Time shift is required",
        });
      }
      return true;
    }),

    greenPower: Yup.number()
      .integer("Green power should be whole number")
      .test("isValidGreenPower", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (current.isGreenPowerRequired && !current.greenPower) {
          return this.createError({
            message: "Green power is required",
          });
        }
        if (current.greenPower <= 0) {
          return this.createError({
            message: "Green power must be positive",
          });
        }
        if (current.greenPower > 100) {
          return this.createError({
            message: "Maximum green power value is 100",
          });
        }
        return true;
      }),
    averageConsumption: Yup.number()
      .integer("Average Consumption should be whole number")
      .test("isValidAverageConsumption", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (current.isGreenPowerRequired && !current.averageConsumption) {
          return this.createError({
            message: "Average Consumption is required",
          });
        }
        if (current.averageConsumption <= 0) {
          return this.createError({
            message: "Average Consumption must be positive",
          });
        }
        if (current.averageConsumption > 100) {
          return this.createError({
            message: "Maximum average Consumption value is 100",
          });
        }
        return true;
      }),
    powerRequirement: Yup.number()
      .required("Contract load is required")
      .test("isValidContractLoad", "", function () {
        const [{ value: current = null } = {}] = this.from || [];
        if (current.powerRequirement <= 0) {
          return this.createError({
            message: "Contract load must be positive",
          });
        }
        if (
          current.tention === "LT" &&
          current.powerRequirement > maxLtContractDemandLoad
        ) {
          return this.createError({
            message: `Maximum value of contract load is ${maxLtContractDemandLoad}`,
          });
        } else if (
          current.tention === "HT" &&
          current.powerRequirement < minHtContractDemandLoad
        ) {
          return this.createError({
            message: "Contract demand should be more than 150",
          });
        } else if (current.powerRequirement > 100000) {
          return this.createError({
            message: "Maximum value allowed for contract demand is 99999",
          });
        }
        return true;
      }),
  }),
});

const contractDemandSchema = Yup.object({
  totalBuildupArea: Yup.number()
    .positive("Total build up area of must be positive")
    .required("Total build up area is required"),
  totalNumberOfOccupants: Yup.number().required(
    "Total number of occupants is required"
  ),
  expectedConnectionStartDate: Yup.date().test(
    "minStartDate",
    "",
    (_, context) => {
      const [
        { value: { expectedConnectionStartDate, applicationTypeId } = {} } = {},
      ] = context.from || [];

      if (applicationTypeId === applicationTypes["Temporary Connection"]) {
        if (!expectedConnectionStartDate) {
          return context.createError({
            message: "Connection start date is required",
          });
        }
        if (
          expectedConnectionStartDate &&
          !expectedConnectionStartDate?.isValid()
        ) {
          return context.createError({
            message: "Enter a valid date",
          });
        }

        if (
          expectedConnectionStartDate
            .startOf("month")
            .isSameOrBefore(moment().add(4, "months"))
        ) {
          return context.createError({
            message: "Connection start date should be after 4 months",
          });
        }
      }

      return true;
    }
  ),
  expectedConnectionEndDate: Yup.date().test("minEndDate", "", (_, context) => {
    const [
      {
        value: {
          expectedConnectionEndDate,
          expectedConnectionStartDate,
          applicationTypeId,
        } = {},
      } = {},
    ] = context.from || [];

    if (applicationTypeId === applicationTypes["Temporary Connection"]) {
      if (!expectedConnectionEndDate) {
        return context.createError({
          message: "Connection end date is required",
        });
      }
      if (expectedConnectionEndDate && !expectedConnectionEndDate?.isValid()) {
        return context.createError({
          message: "Enter a valid date",
        });
      }
      if (
        expectedConnectionStartDate &&
        expectedConnectionEndDate.isSameOrBefore(expectedConnectionStartDate)
      ) {
        return context.createError({
          message: "Connection end date should be after connection start date",
        });
      }
      if (
        expectedConnectionEndDate
          .startOf("month")
          .isSameOrBefore(moment().add(4, "months"))
      )
        return context.createError({
          message: "Connection end date should be after 4 months",
        });
      if (
        expectedConnectionStartDate &&
        expectedConnectionEndDate.isAfter(
          moment(expectedConnectionStartDate)
            .add(1, "years")
            .subtract(1, "days")
        )
      ) {
        return context.createError({
          message:
            "Connection end date should be less than one year from the start date",
        });
      }
    }

    return true;
  }),
  dcsDemand: Yup.object({
    contractDemand: Yup.number().test("isValidContractDemand", "", function () {
      const [
        { value: { contractDemand, ultimateDemand } = {} } = {},
        {
          value: { utilities, extraValues: { isNonDomesticPremise } } = {},
        } = {},
      ] = this.from || [];

      const keyName = isNonDomesticPremise ? "contract demand" : "demand load";

      if (
        utilities.some(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        )
      ) {
        if (!contractDemand) {
          return this.createError({
            message: "Contract demand is required",
          });
        }
        if (contractDemand < 1) {
          return this.createError({
            message: "Enter valid contract demand",
          });
        }
        if (contractDemand > 9999) {
          return this.createError({
            message: "Maximum value for contract demand is 9999",
          });
        }
        if (contractDemand > ultimateDemand) {
          return this.createError({
            message: `Maximum value for ${keyName} is ${ultimateDemand}`,
          });
        }
      }

      return true;
    }),
    ultimateDemand: Yup.number().test("isValidUltimateDemand", "", function () {
      const [
        { value: { ultimateDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      // check for utilities here
      if (
        utilities.some(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        )
      ) {
        if (!ultimateDemand) {
          return this.createError({
            message: "Ultimate Demand is required",
          });
        }
        if (ultimateDemand < 1) {
          return this.createError({
            message: "Enter valid ultimate demand",
          });
        }
        if (ultimateDemand > 9999) {
          return this.createError({
            message: "Maximum value for ultimate demand is 9999",
          });
        }
      }
      return true;
    }),
    chilledWaterLineDiameter: Yup.number().test(
      "isValidChilledWaterLineDiameter ",
      "",
      function () {
        const [
          { value: { chilledWaterLineDiameter } = {} } = {},
          {
            value: {
              utilities,

              utilityAddress: { dcsDomesticPremiseType },
            } = {},
          } = {},
        ] = this.from || [];

        // check for utilities here
        if (
          utilities.some(
            ({ value }) => value === getOptionValue("utilityOptions", "DCS")
          ) &&
          dcsDomesticPremiseType ===
            getOptionValue("dcsConnectionTypeEnum", "Individual Units")
        ) {
          if (!chilledWaterLineDiameter && chilledWaterLineDiameter !== 0) {
            return this.createError({
              message: "Chilled water line diameter is required",
            });
          }

          if (chilledWaterLineDiameter < 0) {
            return this.createError({
              message: "Enter valid chilled water line diameter",
            });
          }
          if (chilledWaterLineDiameter > 999) {
            return this.createError({
              message: "Maximum value for chilled water line diameter is 999",
            });
          }
        }
        return true;
      }
    ),
    waterFlowRate: Yup.number().test("isValidWaterFlowRate", "", function () {
      const [
        { value: { waterFlowRate } = {} } = {},
        {
          value: { utilities, utilityAddress: { dcsDomesticPremiseType } } = {},
        } = {},
      ] = this.from || [];

      // check for utilities here
      if (
        utilities.some(
          ({ value }) => value === getOptionValue("utilityOptions", "DCS")
        ) &&
        dcsDomesticPremiseType ===
          getOptionValue("dcsConnectionTypeEnum", "Individual Units")
      ) {
        if (!waterFlowRate && waterFlowRate !== 0) {
          return this.createError({
            message: "Water flow rate is required",
          });
        }
        if (waterFlowRate < 0) {
          return this.createError({
            message: "Enter valid water flow rate",
          });
        }
        if (waterFlowRate > 999) {
          return this.createError({
            message: "Maximum value for water flow rate is 999",
          });
        }
      }
      return true;
    }),
  }),

  waterSewageDemand: Yup.object({
    waterDemand: Yup.number().test("isValidWaterDemand", "", function () {
      const [
        { value: { waterDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!waterDemand) {
          return this.createError({
            message: "Total water demand is required",
          });
        }
        if (waterDemand > maxWaterDemand) {
          return this.createError({
            message: `Maximum value of total water demand is ${maxWaterDemand}`,
          });
        }
      }
      return true;
    }),
    flushing: Yup.number().test("isValidFlushing", "", function () {
      const [
        { value: { flushing, waterDemand, domestic, gardening } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      const totalOfFlushingDomesticAndGardeningDemand =
        flushing + domestic + gardening;

      // check for utilities here
      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!flushing && flushing !== 0) {
          return this.createError({
            message: "Flushing is required",
          });
        }
        if (flushing < 0) {
          return this.createError({
            message: "Enter valid flushing value",
          });
        }
        if (totalOfFlushingDomesticAndGardeningDemand > waterDemand) {
          return this.createError({
            message: `You have exceeded total water demand limit ${waterDemand} Liters/day`,
          });
        }
      }
      return true;
    }),
    domestic: Yup.number().test("isValidDomestic", "", function () {
      const [
        { value: { domestic, flushing, gardening, waterDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];

      const totalOfFlushingDomesticAndGardeningDemand =
        flushing + domestic + gardening;
      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!domestic && domestic !== 0) {
          return this.createError({
            message: "Domestic is required",
          });
        }
        if (domestic < 0) {
          return this.createError({
            message: "Enter valid domestic value",
          });
        }
        if (totalOfFlushingDomesticAndGardeningDemand > waterDemand) {
          return this.createError({
            message: `You have exceeded total water demand limit ${waterDemand} Liters/day`,
          });
        }
      }
      return true;
    }),
    gardening: Yup.number().test("isValidGardening", "", function () {
      const [
        { value: { gardening, flushing, domestic, waterDemand } = {} } = {},
        { value: { utilities } = {} } = {},
      ] = this.from || [];
      const totalOfFlushingDomesticAndGardeningDemand =
        flushing + domestic + gardening;
      if (
        utilities.some(
          ({ value }) =>
            value === getOptionValue("utilityOptions", "Water") ||
            value === getOptionValue("utilityOptions", "Sewage")
        )
      ) {
        if (!gardening && gardening !== 0) {
          return this.createError({
            message: "Gardening is required",
          });
        }
        if (gardening < 0) {
          return this.createError({
            message: "Enter valid gardening value",
          });
        }

        if (totalOfFlushingDomesticAndGardeningDemand > waterDemand) {
          return this.createError({
            message: `You have exceeded total water demand limit ${waterDemand} Liters/day`,
          });
        }
      }
      return true;
    }),
  }),
});

export const getUtilityConsumerValidation = {
  0: consumerDetailsSchema,
  1: applicantDetailsSchema,
  2: utilityAddressSchema,
  3: connectionLoadSchema,
};

export const getOtherUtilityConsumerValidation = (applicationTypeId) => ({
  0: consumerDetailsSchema,
  1: applicantDetailsSchema,
  2: otherUtilityAddressSchema(applicationTypeId),
  3: contractDemandSchema,
});
